import { Link } from 'react-router-dom';
import { LogoImage } from '../../components/Logo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFingerprint } from '@fortawesome/free-solid-svg-icons';
import './landing.css';

const Tick = () => (
  <svg className="tick" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M3 8.5l3.2 3.2L13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WORKFLOWS = [
  { tag: 'ATF · eForm 1 & 4', title: 'ATF eForm 1 & eForm 4', body: 'Live-scan EFTs for ATF eForm 1 (Make & Register a Firearm) and eForm 4 (Tax-Paid Transfer) — built to FBI EBTS and ready to attach.' },
  { tag: 'DoD · SWFT', title: 'SWFT', body: 'DoD contractor fingerprinting with CAGE-code TCNs and capture-equipment metadata, per DoD EBTS.' },
  { tag: 'FBI · DOCE', title: 'Departmental Order', body: 'Channel DOCE submissions straight to the FBI for applicant and attorney-account processing.' },
  { tag: 'ATF · SWFT', title: 'Card Conversion', body: 'Upload a scanned FD-258 ink card and our card-recognition AI auto-segments all fourteen prints and builds a verified EFT — no live scanner required.' },
];

export function Landing() {
  return (
    <div className="lp grain">
      <div className="lp-bg" />

      <nav className="nav">
        <div className="container nav-inner">
          <Link to="/" className="brand" aria-label="EFTCreator home">
            <LogoImage height={30} />
          </Link>
          <div className="nav-links">
            <a href="#workflows">Workflows</a>
            <a href="#how">How it works</a>
            <a href="#pricing">Pricing</a>
            <a href="#security">Security</a>
          </div>
          <div className="nav-cta">
            <Link to="/app" className="btn btn-ghost btn-sm">Sign in</Link>
            <Link to="/app" className="btn btn-primary btn-sm">Get started</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="hero">
        <div className="container hero-grid">
          <div>
            <span className="eyebrow reveal">Biometric EFT submission platform</span>
            <h1 className="reveal" style={{ animationDelay: '0.08s' }}>
              Capture, validate, and <span className="grad">transmit fingerprints</span> with confidence.
            </h1>
            <p className="lede reveal" style={{ animationDelay: '0.16s' }}>
              EFTCreator turns any workstation and USB scanner into a compliant EFT terminal — ATF, DoD SWFT,
              and FBI DOCE submissions plus ink-card conversion, all from one secure cloud console.
            </p>
            <div className="hero-cta reveal" style={{ animationDelay: '0.24s' }}>
              <Link to="/app" className="btn btn-primary">Start free account →</Link>
              <a href="#how" className="btn btn-ghost">See how it works</a>
            </div>
            <p className="hero-note reveal" style={{ animationDelay: '0.32s' }}>
              // NIST/EBTS compliant · works with your existing scanners
            </p>
          </div>

          <div className="hero-visual" aria-label="Animated fingerprint scan">
            <div className="hero-ridges" aria-hidden />
            <div className="hero-print-glow" />
            <div className="hero-rings" aria-hidden><span /><span /><span /></div>
            <div className="hero-print-wrap">
              <div className="hero-print" aria-hidden />
              <div className="hero-scan" aria-hidden />
              <div className="hero-scanline" aria-hidden />
            </div>
            <div className="hero-chip chip-1"><span className="dot" /> NFIQ 92 · captured</div>
            <div className="hero-chip chip-2">TCN WVATF0900-…</div>
            <div className="hero-chip chip-3"><span className="dot" /> transmitted to FBI</div>
          </div>
        </div>
      </header>

      {/* Trust strip */}
      <div className="trust">
        <div className="container trust-inner">
          <span className="trust-label">Built for submissions to</span>
          <span className="trust-item"><span>ATF</span> eForm 1 &amp; 4</span>
          <span className="trust-item">DoD <span>SWFT</span></span>
          <span className="trust-item">FBI <span>DOCE</span></span>
          <span className="trust-item">FD-258 <span>Conversion</span></span>
        </div>
      </div>

      {/* Workflows */}
      <section className="section" id="workflows">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">One console, every workflow</span>
            <h2>Every fingerprint submission type you handle.</h2>
            <p>Configurable capture workflows drive the right EBTS specification, fields, and routing for each
              agency — so your operators just capture and go.</p>
          </div>
          <div className="wf-grid">
            {WORKFLOWS.map((w, i) => (
              <div className="wf-card reveal" key={w.title} style={{ animationDelay: `${i * 0.07}s` }}>
                <div className="wf-icon"><FontAwesomeIcon icon={faFingerprint} /></div>
                <div className="wf-tag">{w.tag}</div>
                <h3>{w.title}</h3>
                <p>{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section" id="how" style={{ background: 'linear-gradient(180deg, transparent, rgba(19,28,48,0.4), transparent)' }}>
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">Capture → Validate → Transmit</span>
            <h2>From ten-print to transmitted in minutes.</h2>
          </div>
          <div className="steps">
            <div className="step">
              <div className="step-num">01</div>
              <h3>Capture</h3>
              <p>Your local device service drives the scanner — rolled prints, slaps, and quality scoring — while
                demographics auto-fill from a driver's-license scan.</p>
            </div>
            <div className="step">
              <div className="step-num">02</div>
              <h3>Validate</h3>
              <p>Records are built to the exact EBTS spec for the agency, with live NFIQ quality gating and
                field validation before anything leaves the workstation.</p>
            </div>
            <div className="step">
              <div className="step-num">03</div>
              <h3>Transmit</h3>
              <p>Download a ready EFT, or submit DOCE straight to the FBI channel and card conversions to our
                processing pipeline — tracked end to end.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing / licensing */}
      <section className="section" id="pricing">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">Billing that fits how you work</span>
            <h2>Unlimited plans include a scanner license, or pay per transaction.</h2>
            <p>Run high volume on a flat license — your first scanner license is included, add more as you grow —
              or pay only for what you create. Add a scanner license any time (prorated); removals apply at your
              next cycle. (Scanner hardware is sold separately in the Store.)</p>
          </div>
          <div className="price-grid price-grid-4">
            <div className="price-card featured">
              <div className="price-badge">Most popular</div>
              <h3>EFT Creation — Unlimited</h3>
              <div className="price-amount">$85<span>/mo</span></div>
              <div className="price-kicker">// or $765/yr</div>
              <p style={{ color: 'var(--text-dim)', margin: 0 }}>
                Unlimited ATF &amp; SWFT live-scan EFTs. One scanner license is included — add more anytime.
                (Scanner hardware sold separately.)
              </p>
              <ul className="price-list">
                <li><Tick /> Unlimited captures on every licensed scanner</li>
                <li><Tick /> 1 scanner license included; add licenses at $20/mo ($220/yr) each</li>
                <li><Tick /> Add or remove scanner licenses self-service</li>
              </ul>
            </div>
            <div className="price-card">
              <h3>EFT Creation — Pay-per-tx</h3>
              <div className="price-amount">$25<span>/ EFT</span></div>
              <div className="price-kicker">// metered, no device limits</div>
              <p style={{ color: 'var(--text-dim)', margin: 0 }}>
                Only pay for each EFT you produce — perfect for mobile and occasional fingerprinting.
              </p>
              <ul className="price-list">
                <li><Tick /> No per-device commitment</li>
                <li><Tick /> Any scanner, any location</li>
                <li><Tick /> Great for remote &amp; low-volume sites</li>
              </ul>
            </div>
            <div className="price-card">
              <h3>Card Conversion</h3>
              <div className="price-amount">$30<span>/ card</span></div>
              <div className="price-kicker">// prepaid credits or metered</div>
              <p style={{ color: 'var(--text-dim)', margin: 0 }}>
                AI-segmented FD-258 ink-card conversion. Buy credits as you go or subscribe to a metered plan.
              </p>
              <ul className="price-list">
                <li><Tick /> Automatic 14-print segmentation</li>
                <li><Tick /> EBTS-verified before delivery</li>
                <li><Tick /> Prepaid credits or metered billing</li>
              </ul>
            </div>
            <div className="price-card">
              <h3>FBI IdHS (DOCE)</h3>
              <div className="price-amount">$50<span>/ tx</span></div>
              <div className="price-kicker">// electronic FBI channeling</div>
              <p style={{ color: 'var(--text-dim)', margin: 0 }}>
                Submit fingerprint-based FBI Identity History Summary (IdHS) checks electronically through the DOCE channel.
              </p>
              <ul className="price-list">
                <li><Tick /> Electronic submission to the FBI (DOCE)</li>
                <li><Tick /> From live-scan or a converted card</li>
                <li><Tick /> Provisioned per account</li>
              </ul>
            </div>
          </div>
          <div className="price-strip">
            <span className="price-strip-icon"><FontAwesomeIcon icon={faFingerprint} /></span>
            <div>
              <strong>Secure EFT storage — from $9.99/mo.</strong>{' '}
              <span style={{ color: 'var(--text-dim)' }}>Automatically archive every EFT under your account and
                search it by name or date of birth. Tiered by volume: Starter (1,000) · Professional (5,000) ·
                Business (25,000) · Enterprise (100,000).</span>
            </div>
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="section" id="security" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">Compliance-grade by design</span>
            <h2>Security your compliance team will approve.</h2>
          </div>
          <div className="sec-grid">
            <div className="sec-item">
              <h4><Tick /> NIST / EBTS compliant</h4>
              <p>Records built to the current FBI and DoD EBTS specifications, validated before transmission.</p>
            </div>
            <div className="sec-item">
              <h4><Tick /> Tenant-isolated & encrypted</h4>
              <p>Every account is isolated; data encrypted at rest and in transit, with role-based access for your team.</p>
            </div>
            <div className="sec-item">
              <h4><Tick /> Multi-user accounts</h4>
              <p>Administrators invite operators with scoped permissions and manage devices, licenses, and locations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <div className="container">
        <div className="cta-band grain">
          <span className="eyebrow" style={{ justifyContent: 'center' }}>Ready when you are</span>
          <h2>Set up your account and capture your first EFT today.</h2>
          <p>Create an account, connect your scanner, and start submitting — your data comes with you.</p>
          <Link to="/app" className="btn btn-primary">Get started free →</Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer-inner">
          <LogoImage height={24} />
          <span>© {'2026'} Lakota Software · Biometric EFT platform</span>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <a href="#pricing">Pricing</a>
            <Link to="/legal/terms">Terms</Link>
            <Link to="/legal/privacy">Privacy</Link>
            <Link to="/legal/returns">Returns</Link>
            <Link to="/app">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
